/**
 * Halt the loop — chrome-level destructive control. Replaces the always-pulsing
 * 224×92 EMERGENCY STOP rectangle with an inline header button that requires
 * typing HALT to confirm, plus the existing reauth flow, plus a 15-second
 * idle auto-cancel.
 *
 * Phase 2 of the frontend redesign — see
 * docs/plans/2026-04-28-frontend-redesign.md §2.4, §4.4, and the Phase 2
 * acceptance criteria in §7.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import * as api from '../../api/client'
import { ReauthDialog, useReauth } from '../common/ReauthDialog'
import { useToast } from '../../hooks/useToast'
import { useTradingStore } from '../../state/trading'

const AUTO_CANCEL_SECONDS = 15
const REQUIRED_PHRASE = 'HALT'

interface HaltTheLoopProps {
  /** Override the default emergency-stop endpoint call — used by tests. */
  onHalt?: () => Promise<void>
}

export function HaltTheLoop({ onHalt }: HaltTheLoopProps = {}) {
  const [open, setOpen] = useState(false)
  const [phrase, setPhrase] = useState('')
  const [secondsLeft, setSecondsLeft] = useState(AUTO_CANCEL_SECONDS)
  const [submitting, setSubmitting] = useState(false)
  const [reauthPending, setReauthPending] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const lastActivityRef = useRef<number>(0)
  const haltRequestActiveRef = useRef(false)
  const { requireReauth, reauthDialog } = useReauth()
  const { toast } = useToast()

  const close = useCallback(() => {
    haltRequestActiveRef.current = false
    setOpen(false)
    setPhrase('')
    setSecondsLeft(AUTO_CANCEL_SECONDS)
    setSubmitting(false)
    setReauthPending(false)
  }, [])

  // Idle countdown — auto-cancel after AUTO_CANCEL_SECONDS of inactivity.
  // The lastActivityRef is bumped on every keystroke (see handlePhraseChange).
  // The setState inside setInterval is the standard idiom for a wall-clock
  // ticker; the react-hooks/set-state-in-effect rule misfires here.
  useEffect(() => {
    if (!open) return
    if (submitting) return
    if (reauthPending) return
    lastActivityRef.current = Date.now()
    // eslint-disable-next-line react-hooks/set-state-in-effect -- setting initial seconds when the dialog opens
    setSecondsLeft(AUTO_CANCEL_SECONDS)
    const interval = setInterval(() => {
      const elapsed = (Date.now() - lastActivityRef.current) / 1000
      const remaining = Math.max(0, AUTO_CANCEL_SECONDS - Math.floor(elapsed))
      setSecondsLeft(remaining)
      if (remaining <= 0) {
        clearInterval(interval)
        close()
      }
    }, 250)
    return () => clearInterval(interval)
  }, [open, submitting, reauthPending, close])

  const handlePhraseChange = (value: string) => {
    setPhrase(value)
    lastActivityRef.current = Date.now()
  }

  // Focus the input when the dialog opens.
  useEffect(() => {
    if (open) {
      const f = requestAnimationFrame(() => inputRef.current?.focus())
      return () => cancelAnimationFrame(f)
    }
  }, [open])

  // Escape closes the dialog.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, close])

  const phraseMatches = phrase.trim().toUpperCase() === REQUIRED_PHRASE
  const canSubmit = phraseMatches && !submitting && !reauthPending

  const handleSubmit = () => {
    if (!canSubmit) return
    haltRequestActiveRef.current = true
    setReauthPending(true)
    requireReauth(async () => {
      if (!haltRequestActiveRef.current) return
      setSubmitting(true)
      setReauthPending(false)
      try {
        if (onHalt) {
          await onHalt()
	        } else {
	          await api.post('/system/emergency-stop', { reason: 'operator_chrome' })
	        }
	        useTradingStore.getState().markSystemHalted()
	        toast.success(
          'Loop halted. Open orders were not broker-cancelled here; check broker status now.',
        )
        close()
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Halt failed — please retry.')
        setSubmitting(false)
        setReauthPending(false)
      }
    }, 'halt the loop and require manual broker order review')
  }

  const guardedReauthDialog = {
    ...reauthDialog,
    onCancel: () => {
      haltRequestActiveRef.current = false
      setReauthPending(false)
      reauthDialog.onCancel()
    },
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="font-mono text-[11px] uppercase tracking-[0.08em] font-medium text-muted hover:text-risk hover:border-risk border border-rule-2 px-3.5 py-1.5 transition-colors"
        aria-label="Halt the loop"
        data-testid="halt-the-loop-trigger"
      >
        Halt the loop
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="halt-dialog-title"
          aria-describedby="halt-dialog-desc"
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 backdrop-blur-sm p-6"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) close()
          }}
        >
          <div className="w-full max-w-md border border-rule-2 bg-ink p-8 font-serif">
            <h2 id="halt-dialog-title" className="text-2xl font-medium leading-tight text-bone">
              Halt the loop and review broker orders?
            </h2>
            <p id="halt-dialog-desc" className="mt-3 text-[15px] leading-relaxed text-muted">
              This will immediately prevent new candidates from being submitted. It does not confirm
              broker cancellation; check open orders at the broker before assuming any order is
              closed. Type <span className="font-mono text-bone">{REQUIRED_PHRASE}</span> to
              confirm.
            </p>

            <input
              ref={inputRef}
              type="text"
              value={phrase}
              onChange={(e) => handlePhraseChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canSubmit) handleSubmit()
              }}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              placeholder={REQUIRED_PHRASE}
              aria-label="Type HALT to confirm"
              data-testid="halt-the-loop-phrase"
              className="mt-6 w-full bg-transparent border-b border-rule-2 px-0 py-2 font-mono text-base text-bone placeholder:text-dim focus:border-amber focus:outline-none"
            />

            <div className="mt-8 flex items-center justify-between">
              <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-dim">
                {reauthPending ? 'Re-authentication pending' : `Auto-cancel in ${secondsLeft}s`}
              </span>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={close}
                  className="font-mono text-[11px] uppercase tracking-[0.08em] text-muted hover:text-bone px-3.5 py-1.5 border border-rule-2 hover:border-rule-2"
                  data-testid="halt-the-loop-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className="font-mono text-[11px] uppercase tracking-[0.08em] font-medium px-3.5 py-1.5 border transition-colors text-risk border-risk hover:bg-risk hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-risk"
                  data-testid="halt-the-loop-confirm"
                >
                  {submitting ? 'Halting…' : reauthPending ? 'Reauthorizing…' : 'Halt trading'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ReauthDialog {...guardedReauthDialog} />
    </>
  )
}
